/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2022, Zumper
 * @description CarouselV4 component
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import './CarouselV4.css'
import { ProgressDots } from './ProgressDots'

export interface CarouselV4DotsProps {
  /** 0 is the first slide */
  currentSlide: number
  dotsCustomClassName?: string
  /** place dots in a gray pill, to ensure visibility against light backgrounds */
  inPill?: boolean
  goToSlide: ((index: number) => void) | undefined
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  showNumbers?: boolean
  totalItems: number
}

export const CarouselV4Dots = ({
  dotsCustomClassName,
  inPill,
  onKeyDown,
  showNumbers,
  ...props
}: CarouselV4DotsProps) => {
  const firstVisibleDotIndex =
    props.currentSlide < 1
      ? 0
      : Math.max(0, Math.min(props.totalItems - 4, props.currentSlide - 1))
  const dotsRef = useRef<HTMLDivElement | null>(null)
  const textRef = useRef<HTMLParagraphElement | null>(null)
  const [animate, setAnimate] = useState(false)
  const [dots, setDots] = useState<HTMLDivElement | null>(null)
  const onAnimationEnd = useCallback(() => {
    setAnimate(false)
  }, [])
  useEffect(() => {
    dotsRef.current = dots
    if (showNumbers && dots) {
      dots.addEventListener('animationend', onAnimationEnd)
      return () => {
        dots.removeEventListener('animationend', onAnimationEnd)
      }
    }
  }, [dots, onAnimationEnd, showNumbers])
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (showNumbers) {
      setAnimate(true)
    } else {
      setAnimate(false)
    }
  }, [showNumbers, props.currentSlide, props.totalItems])
  const disableDot = useCallback(
    (index: number) => {
      return index < firstVisibleDotIndex || index > firstVisibleDotIndex + 3
    },
    [firstVisibleDotIndex]
  )
  const contents = showNumbers ? (
    <div className={twMerge(animate && 'dotsAnimation')} ref={setDots}>
      <ProgressDots
        {...props}
        disabled={disableDot}
        firstVisibleDotIndex={firstVisibleDotIndex}
      />
    </div>
  ) : (
    <ProgressDots
      {...props}
      disabled={disableDot}
      firstVisibleDotIndex={firstVisibleDotIndex}
    />
  )

  return (
    <div
      className={twMerge(
        'absolute bottom-2 left-1/2 px-1 py-0.5 rounded-[159984px] transform -translate-x-1/2',
        inPill && 'backdrop-blur-[4px] bg-[rgba(0, 0, 0, 0.5)]',
        dotsCustomClassName
      )}
      onKeyDown={onKeyDown}
      title={`Item ${props.currentSlide + 1} of ${props.totalItems}`}
    >
      <div className="dotContent max-w-[44px] overflow-hidden">{contents}</div>
      {showNumbers && (
        <p
          aria-hidden
          className={twMerge(
            'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-0 text-[12px] font-bold opacity-0 pointer-events-none whitespace-nowrap m-0 shadow-[0_0_5px_#000]',
            animate && 'showTextAnimation'
          )}
          ref={textRef}
        >
          {1 + props.currentSlide} / {props.totalItems}
        </p>
      )}
    </div>
  )
}
